/**
 * Lender pipelines — the broker's loans as each lender's own portal shows them (Spring EQ's
 * EMMA, PennyMac's POWER+ …), normalised so every lender's pipeline lists, sorts and alerts the
 * same way next to the UWM pipeline. Shared by the sync (lender-sheets), the API and the page.
 */

/** Where a loan stands, in the order a loan moves. */
export type LenderStage = 'registered' | 'submitted' | 'in_review' | 'approved' | 'clear_to_close' | 'docs_out' | 'funded' | 'suspended' | 'denied' | 'withdrawn' | 'unknown';
export const LENDER_STAGE_ORDER: readonly LenderStage[] = ['registered', 'submitted', 'in_review', 'approved', 'clear_to_close', 'docs_out', 'funded', 'suspended', 'denied', 'withdrawn', 'unknown'];
export const LENDER_STAGE_LABELS: Record<LenderStage, string> = {
  registered: 'Registered',
  submitted: 'Submitted / processing',
  in_review: 'In underwriting',
  approved: 'Approved with conditions',
  clear_to_close: 'Clear to close',
  docs_out: 'Docs out / closing',
  funded: 'Funded',
  suspended: 'Suspended',
  denied: 'Denied',
  withdrawn: 'Withdrawn / cancelled',
  unknown: 'Unknown',
};
/** Stages a loan is still moving through (the default pipeline view). */
export const LENDER_OPEN_STAGES: readonly LenderStage[] = ['registered', 'submitted', 'in_review', 'approved', 'clear_to_close', 'docs_out', 'suspended', 'unknown'];

export type LenderLockStatus = 'locked' | 'floating' | 'requested' | 'expired' | 'unknown';
export const LENDER_LOCK_LABELS: Record<LenderLockStatus, string> = { locked: 'Locked', floating: 'Floating', requested: 'Lock requested', expired: 'Lock expired', unknown: '—' };

/** What the sync records when a loan appears, moves or leaves. */
export type LenderLoanEventType = 'new' | 'status_changed' | 'lock_changed' | 'lock_expiring' | 'closing_scheduled' | 'gone';
export const LENDER_LOAN_EVENT_LABELS: Record<LenderLoanEventType, string> = {
  new: 'New loan',
  status_changed: 'Status changed',
  lock_changed: 'Lock changed',
  lock_expiring: 'Lock expiring',
  closing_scheduled: 'Closing scheduled',
  gone: 'No longer listed',
};

/** One loan as a lender's portal reports it, normalised (every field the portal lacks is null). */
export interface LenderLoanRecord {
  /** The portal's own id (EMMA loan id, Encompass loan GUID). */
  externalId: string;
  loanNumber: string | null;
  borrowerName: string | null;
  coBorrowerName: string | null;
  propertyAddress: string | null;
  propertyCity: string | null;
  propertyState: string | null;
  propertyZip: string | null;
  loanAmount: number | null;
  loanRate: number | null;
  ltv: number | null;
  loanProduct: string | null;
  loanPurpose: string | null;
  loanType: string | null;
  occupancy: string | null;
  /** The portal's own status / milestone text. */
  status: string | null;
  statusDetail: string | null;
  statusAt: Date | null;
  stage: LenderStage;
  lockStatus: LenderLockStatus;
  lockStatusRaw: string | null;
  lockDate: Date | null;
  lockExpiresAt: Date | null;
  closingAt: Date | null;
  hasOpenConditions: boolean | null;
  loanOfficer: string | null;
  portalUrl: string | null;
  /** Everything the portal sent for the loan, for the detail view. */
  raw: Record<string, unknown>;
}

/** The stage a portal's status / milestone / folder words mean. */
export function stageOf(...texts: Array<string | null | undefined>): LenderStage {
  const t = texts
    .filter((x): x is string => !!x)
    .join(' ')
    .toLowerCase();
  if (!t.trim()) return 'unknown';
  if (/withdraw|cancel|adverse|trash|closed for incompleteness/.test(t)) return 'withdrawn';
  if (/denied|declin/.test(t)) return 'denied';
  if (/suspend/.test(t)) return 'suspended';
  if (/fund|purchased|completion|complete\b|closed\b|post.?clos/.test(t)) return 'funded';
  if (/docs? (sent|out|signing)|closing|signing|clos/.test(t)) return 'docs_out';
  if (/clear to close|\bctc\b|cleared/.test(t)) return 'clear_to_close';
  if (/approv|conditional|cond\.|conditions/.test(t)) return 'approved';
  if (/underwrit|\buw\b|in review|review|resubmit/.test(t)) return 'in_review';
  if (/submit|process|setup|set-up|received|disclos|appraisal|in progress/.test(t)) return 'submitted';
  if (/regist|new|started|start|open|prospect|application|pre-?qual/.test(t)) return 'registered';
  return 'unknown';
}

/** The lock status a portal's lock words mean. */
export function lockStatusOf(...texts: Array<string | null | undefined>): LenderLockStatus {
  const t = texts
    .filter((x): x is string => !!x)
    .join(' ')
    .toLowerCase();
  if (!t.trim()) return 'unknown';
  if (/expired/.test(t)) return 'expired';
  if (/not locked|unlock|float/.test(t)) return 'floating';
  if (/request|pending/.test(t)) return 'requested';
  if (/locked|lock confirm|\block\b/.test(t)) return 'locked';
  return 'unknown';
}

/** The instant of a wall-clock time in a zone (its offset read at that moment; DST-safe away from the switch hour). */
export function instantInZone(zone: string, y: number, mo: number, d: number, h = 12, mi = 0, s = 0): Date {
  const naive = Date.UTC(y, mo - 1, d, h, mi, s);
  const tz = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date(naive)).find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = tz.match(/GMT([+-])(\d+)(?::(\d+))?/);
  const offsetMin = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 0;
  return new Date(naive - offsetMin * 60_000);
}

/**
 * A portal date string → instant in the portal's zone: "2026-09-20 14:33:12", "2026-09-30",
 * "09/30/2026 5:00:00 PM", "9/30/2026". Date-only values land at noon so "days until" is whole.
 */
export function portalDate(value: unknown, zone: string): Date | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) return instantInZone(zone, Number(m[1]), Number(m[2]), Number(m[3]), m[4] != null ? Number(m[4]) : 12, Number(m[5] ?? 0), Number(m[6] ?? 0));
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i);
  if (m) {
    let h = m[4] != null ? Number(m[4]) : 12;
    if (m[7]) h = (h % 12) + (m[7].toUpperCase() === 'PM' ? 12 : 0);
    return instantInZone(zone, Number(m[3]), Number(m[1]), Number(m[2]), h, Number(m[5] ?? 0), Number(m[6] ?? 0));
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "$ 500,000.00" / "6.500%" / "80.00% / 85.00%" → the first number, else null. */
export function portalNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const m = String(value).replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return m ? Number(m[0]) : null;
}
